import type React from "react";
import { useForm } from "react-hook-form";

import type { OfferedAnswerDto, PollDto } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { CheckDone02Icon } from "@/components/Icons/CheckDone02Icon";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { twResolve } from "@/helpers/tw-resolve";
import { pollMutations } from "@/queries/polls/mutations";
import { useTranslation } from "@/translations";

import { CommunityPollVoteAnswersInput } from "./CommunityPollVoteAnswersInput";

interface CommunityPollVoteFormProps {
  poll: PollDto;
  answers?: OfferedAnswerDto[];
}

export type CommunityPollVoteFormValues = {
  votes: string[];
};

export function CommunityPollVoteForm({ poll, answers }: CommunityPollVoteFormProps): React.ReactNode {
  const form = useForm<CommunityPollVoteFormValues>({
    defaultValues: {
      votes: [],
    },
    mode: "onSubmit",
  });
  const { t } = useTranslation();

  const addPollVotesMutation = pollMutations.useAddPollVotes();

  const handleSubmit = async (data: CommunityPollVoteFormValues) => {
    await addPollVotesMutation.mutateAsync({
      pollId: poll.id,
      data,
    });
  };

  const isFormInvalid = Boolean(form.formState.errors.votes);

  return (
    <Stack gap="md">
      {poll.allowMultipleAnswers && (
        <span
          data-testid="community-poll-allow-multiple-answers-indicator"
          className={twResolve("flex w-full gap-space-xs text-text-secondary", isFormInvalid && "text-text-danger")}
        >
          <CheckDone02Icon size={24} />
          <span className="text-caption">{t("component.community-poll.form.label")}</span>
        </span>
      )}

      <Form.Root formMethods={form} onSubmit={handleSubmit} fullWidth gap="sm">
        <CommunityPollVoteAnswersInput
          offeredAnswers={answers ?? poll.answers}
          allowMultipleAnswers={poll.allowMultipleAnswers}
        />

        <Button
          fullWidth
          isLoading={addPollVotesMutation.isPending}
          type="submit"
          data-testid="community-poll-vote-btn"
        >
          {t("component.community-poll.button.vote")}
        </Button>
      </Form.Root>
    </Stack>
  );
}
